import { useState } from "react";
import { useMutation } from "convex/react";
import { Avatar, Button, ButtonGroup, Card, HStack, Heading, Stack, Text } from "@chakra-ui/react";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "@convex/_generated/dataModel";
import { SinglePersonPicker } from "../components/SinglePersonPicker";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { useEffectivePersonId } from "../impersonation";

type HeadedGroup = FunctionReturnType<typeof api.delegations.forHead>[number];

function formatDay(day: string) {
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(new Date(`${day}T12:00:00Z`));
}

function GroupDelegates({ group, showName }: { group: HeadedGroup; showName: boolean }) {
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;
  const nameDelegate = useMutation(api.delegations.nameDelegate);
  const endDelegation = useMutation(api.delegations.endDelegation);
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [ending, setEnding] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function add() {
    if (chosen === null) {
      return;
    }
    setSaving(true);
    try {
      await nameDelegate({ effectivePersonId, orgUnitId: group.orgUnitId, delegatePersonId: chosen as Id<"persons"> });
      toastSuccess("Delegate named.");
      setAdding(false);
      setChosen(null);
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Could not name the delegate.");
    } finally {
      setSaving(false);
    }
  }

  async function end(delegationId: string) {
    setSaving(true);
    try {
      await endDelegation({ effectivePersonId, delegationId: delegationId as Id<"delegations"> });
      toastSuccess("Delegation ended.");
      setEnding(null);
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Could not end the delegation.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack gap={3}>
      {showName ? <Text fontSize="sm" fontWeight="semibold">{group.unitName}</Text> : null}
      {group.delegates.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">No delegates.</Text>
      ) : (
        <Stack gap={2}>
          {group.delegates.map((row) => (
            <HStack key={row.delegationId} justify="space-between" gap={3} flexWrap="wrap">
              <HStack gap={3}>
                <Avatar.Root size="sm">
                  <Avatar.Fallback name={row.person!.fullName} />
                  {row.person!.profileImageUrl ? <Avatar.Image src={row.person!.profileImageUrl} alt="" /> : null}
                </Avatar.Root>
                <Stack gap={0}>
                  <Text fontSize="sm" fontWeight="medium">{row.person!.fullName}</Text>
                  <Text fontSize="xs" color="fg.muted">Since {formatDay(row.grantedOn)}</Text>
                </Stack>
              </HStack>
              {ending === row.delegationId ? (
                <ButtonGroup size="xs" gap={2}>
                  <Button colorPalette="orange" loading={saving} onClick={() => void end(row.delegationId)}>End delegation</Button>
                  <Button variant="ghost" onClick={() => setEnding(null)}>Cancel</Button>
                </ButtonGroup>
              ) : (
                <Button size="xs" variant="outline" onClick={() => setEnding(row.delegationId)}>End</Button>
              )}
            </HStack>
          ))}
        </Stack>
      )}

      {adding ? (
        <Stack gap={3}>
          <SinglePersonPicker
            label="New delegate"
            people={group.candidates}
            selectedPersonId={chosen}
            onChange={setChosen}
          />
          <ButtonGroup size="sm" gap={2}>
            <Button colorPalette="blue" disabled={chosen === null} loading={saving} onClick={() => void add()}>Save</Button>
            <Button variant="ghost" onClick={() => { setAdding(false); setChosen(null); }}>Cancel</Button>
          </ButtonGroup>
        </Stack>
      ) : (
        <HStack>
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>Add delegate</Button>
        </HStack>
      )}
    </Stack>
  );
}

/**
 * The head's delegates, on My group.
 *
 * Only the head of a top-level group sees this, and only the head names or
 * ends a delegate; the query returns nothing for anybody else. Not on the
 * public group page: who acts for the head is the head's business, recorded
 * in the audit log and the activity history rather than displayed.
 */
export function DelegatesCard() {
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;
  const groups = useQuery(api.delegations.forHead, { effectivePersonId });

  if (!groups || groups.length === 0) {
    return null;
  }

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={4} p={5}>
        <Stack gap={1}>
          <Heading size="md">Delegates</Heading>
          <Text fontSize="sm" color="fg.muted">
            Delegates act for you on activities: they review and sign off for your group, and see
            it here. Only members of the group can be named, and only you see this list.
          </Text>
        </Stack>
        {groups.map((group) => (
          <GroupDelegates key={group.orgUnitId} group={group} showName={groups.length > 1} />
        ))}
      </Card.Body>
    </Card.Root>
  );
}
